import React, { useEffect, useState } from 'react';
import { CharacterDefinition, CharacterCreationStepEnum, CharacterRosterEntryDTO, GameSummaryDTO } from '@hexcrawl/shared-utils';
import { AppearancePreview } from '../character/appearance/AppearancePreview';
import { fetchCharacterDefinition } from '../../services/characterDefinitionService';

const CARD_CONTENT_WIDTH_PX = 160;
const CARD_HORIZONTAL_PADDING_PX = 14;
const CARD_BORDER_PX = 1;

/**
 * Full rendered width of a card, padding and border included — the cards are content-box, so this is
 * wider than the declared width. Exported because the lobby aligns other elements to the card row.
 */
export const CHARACTER_CARD_OUTER_WIDTH_PX =
  CARD_CONTENT_WIDTH_PX + 2 * (CARD_HORIZONTAL_PADDING_PX + CARD_BORDER_PX);

interface Props {
  character: CharacterRosterEntryDTO | null;
  onCreateCharacter: () => void;
  onResumeCreation: (character: CharacterRosterEntryDTO) => void;
  onBeginAdventure: (characterId: string) => void;
  onContinueAdventure: (game: GameSummaryDTO) => void;
  onViewNpcs?: (character: CharacterRosterEntryDTO) => void;
  disabled?: boolean;
}

export const CharacterCard: React.FC<Props> = ({
  character,
  onCreateCharacter,
  onResumeCreation,
  onBeginAdventure,
  onContinueAdventure,
  onViewNpcs,
  disabled,
}) => {
  if (!character) {
    return (
      <div
        style={{...cardStyle, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1}}
        onClick={!disabled ? onCreateCharacter : undefined}
      >
        <div style={{fontSize: 40, opacity: 0.4, lineHeight: 1}}>+</div>
        <div style={{fontSize: 12, opacity: 0.5, marginTop: 8}}>Create Character</div>
      </div>
    );
  }

  const [definition, setDefinition] = useState<CharacterDefinition | null>(null);

  useEffect(() => {
    if (!character.appearance) return;
    fetchCharacterDefinition(character.id)
      .then(setDefinition)
      .catch(e => console.warn('[CharacterCard] Failed to fetch character definition:', e));
  }, [character.id]);

  const activeGame = character.activeGame;
  const isComplete = character.creationStep === CharacterCreationStepEnum.COMPLETE;

  return (
    <div style={{...cardStyle, cursor: 'default'}}>
      <div style={{fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: '#e8e8ff', textAlign: 'center'}}>
        {isComplete ? character.name : <em style={{color: '#aaa', fontStyle: 'italic'}}>In Progress…</em>}
      </div>

      <div style={{fontSize: 11, color: '#888', marginBottom: 10, textAlign: 'center'}}>
        {isComplete ? `Level ${character.level}` : 'Creation incomplete'}
      </div>

      {character.appearance && (
        <div style={{marginBottom: 10}}>
          <AppearancePreview
            definition={definition}
            size={130}
          />
        </div>
      )}

      {/* Status badge */}
      {isComplete && !activeGame && (
        <div style={availableBadgeStyle}>Available</div>
      )}
      {isComplete && activeGame && (
        <div style={adventuringBadgeStyle}>
          Day {activeGame.day} / {activeGame.maxDays}
        </div>
      )}

      <div style={{flex: 1}}/>

      {/* Action button */}
      {!isComplete && (
        <button style={btnStyle} disabled={disabled} onClick={() => onResumeCreation(character)}>
          Resume Creation
        </button>
      )}
      {isComplete && !activeGame && (
        <button style={btnStyle} disabled={disabled} onClick={() => onBeginAdventure(character.id)}>
          Begin Adventure
        </button>
      )}
      {isComplete && activeGame && (
        <button style={btnStyle} disabled={disabled} onClick={() => onContinueAdventure(activeGame)}>
          Continue
        </button>
      )}
      {isComplete && onViewNpcs && (
        <button style={secondaryBtnStyle} disabled={disabled} onClick={() => onViewNpcs(character)}>
          NPCs
        </button>
      )}
    </div>
  );
};

const cardStyle: React.CSSProperties = {
  background: '#1a1a2e',
  border: `${CARD_BORDER_PX}px solid #334`,
  borderRadius: 8,
  padding: `16px ${CARD_HORIZONTAL_PADDING_PX}px`,
  width: CARD_CONTENT_WIDTH_PX,
  minHeight: 190,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
};

const availableBadgeStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 'bold',
  letterSpacing: 0.5,
  background: '#1a2a1a',
  border: '1px solid #3a5a3a',
  borderRadius: 4,
  padding: '3px 8px',
  color: '#88cc88',
  marginBottom: 10,
  textTransform: 'uppercase',
};

const adventuringBadgeStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 'bold',
  letterSpacing: 0.5,
  background: '#1a1f2a',
  border: '1px solid #3a5070',
  borderRadius: 4,
  padding: '3px 8px',
  color: '#88aacc',
  marginBottom: 10,
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '4px 12px',
  borderRadius: 4,
  border: '1px solid #445',
  background: 'transparent',
  color: '#aaa',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'monospace',
  width: '100%',
  marginTop: 4,
};

const btnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 4,
  border: 'none',
  background: '#4CAF50',
  color: 'white',
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'monospace',
  width: '100%',
  marginTop: 4,
};
